"use client";

import React, { useState } from "react";
import { Button } from "@/shadcn-components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import {
  Columns2,
  LayoutGrid,
  Rows2,
  Settings,
  Square,
  Sun,
  Moon,
  PanelLeft,
  PanelBottom,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useActiveAccount } from "@/lib/accounts/store";
import { useConnectionProgressStore } from "@/vybe-trader-client/connection-progress/stores/useConnectionProgressStore";
import { useTheme } from "@/contexts/theme-context";
import {
  useDesktopLayoutStore,
  type DesktopChartLayoutMode,
} from "@/lib/desktop-layout/store";
import { AccountsSheet } from "./accounts-content/AccountsSheet";
import { BrokerLogo } from "@/components/common/BrokerLogo";
import { SettingsSheet } from "./settings-content/SettingsSheet";
import { HeaderUserChip } from "./HeaderUserChip";
import { VybeLogo } from "@/components/common";
import { AccountMetricsStrip } from "@/components/account-information";
import { InvestorBadge } from "@/components/common/InvestorBadge";
import { VybeAgentMark } from "@/components/desktop/agent";
import { useVybeStudioStore } from "@/lib/custom-indicators/studio-store";
import { useIsAdmin } from "@/lib/vybe-auth";
import { Sparkles } from "lucide-react";

// Running inside the Tauri desktop shell (any OS): the native title bar is hidden,
// so non-interactive header regions double as window drag handles via
// `data-tauri-drag-region`. Web keeps normal pointer behaviour.
const isTauri =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
// macOS additionally floats the traffic-light buttons over this bar (titleBarStyle
// "Overlay"), so the left content must clear them.
const isTauriMac =
  isTauri && navigator.platform.toLowerCase().includes("mac");
// Attribute spread: present only under Tauri (a bare data-* attr is enough — the
// plugin checks for its presence on the mousedown target).
const dragRegion = isTauri ? { "data-tauri-drag-region": "" } : {};

export default function HeaderDesktop() {
  const [isAccountsSheetOpen, setIsAccountsSheetOpen] = useState(false);
  const [isSettingsSheetOpen, setIsSettingsSheetOpen] = useState(false);

  // Theme hook
  const { setTheme, actualTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(actualTheme === "light" ? "dark" : "light");
  };

  // Desktop layout visibility toggles (primitive selectors to avoid extra re-renders)
  const quotesVisible = useDesktopLayoutStore((s) => s.quotesVisible);
  const footerVisible = useDesktopLayoutStore((s) => s.footerVisible);
  const agentSidebarVisible = useDesktopLayoutStore(
    (s) => s.agentSidebarVisible,
  );
  const toggleQuotes = useDesktopLayoutStore((s) => s.toggleQuotes);
  const toggleFooter = useDesktopLayoutStore((s) => s.toggleFooter);
  const toggleAgentSidebar = useDesktopLayoutStore((s) => s.toggleAgentSidebar);
  const chartLayoutMode = useDesktopLayoutStore((s) => s.chartLayoutMode);
  const setChartLayoutMode = useDesktopLayoutStore(
    (s) => s.setChartLayoutMode,
  );

  const studioOpen = useVybeStudioStore((s) => s.open);
  const setStudioOpen = useVybeStudioStore((s) => s.setOpen);

  // Vybe Studio is admin-only (client-side UX gating, not a security boundary).
  const isAdmin = useIsAdmin();

  const chartLayoutLabels: Record<DesktopChartLayoutMode, string> = {
    single: "Single chart",
    "two-horizontal": "Two charts (horizontal)",
    "two-vertical": "Two charts (vertical)",
    quad: "Four charts (grid)",
  };

  // Get active account data
  const activeAccount = useActiveAccount();

  // Get connection status
  const isConnected = useConnectionProgressStore((state) => state.isConnected);
  const isConnecting = useConnectionProgressStore(
    (state) => state.isConnecting
  );

  // Determine connection status and color
  const getConnectionStatus = () => {
    if (isConnected) {
      return {
        status: "Connected",
        color: "bg-green-500",
      };
    } else if (isConnecting) {
      return {
        status: "Connecting",
        color: "bg-yellow-500",
      };
    } else {
      return {
        status: "Disconnected",
        color: "bg-red-500",
      };
    }
  };

  const connectionInfo = getConnectionStatus();

  const createTriggerButton = () => {
    if (activeAccount) {
      return (
        <Button
          variant="outline"
          size="sm"
          className="text-xs cursor-pointer hover:bg-muted space-x-2 flex h-5 px-2"
          title={`${activeAccount.companyName} · ${activeAccount.serverName}`}
        >
          <div className={`w-2 h-2 rounded-full ${connectionInfo.color}`} />
          <BrokerLogo
            companyName={activeAccount.companyName}
            logoUrl={activeAccount.brokerLogoUrl}
            className="w-4 h-4 rounded"
            logoClassName="w-3.5 h-3.5"
          />
          <span>{activeAccount.credentials.login}</span>
        </Button>
      );
    } else {
      return (
        <Button
          variant="outline"
          size="sm"
          className="text-xs cursor-pointer hover:bg-muted space-x-2 flex h-5 px-2"
        >
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-muted-foreground">No Account</span>
        </Button>
      );
    }
  };

  return (
    <div
      data-tauri-drag-region
      className={cn(
        "h-10 w-full bg-background border-b border-border flex items-center",
        // Clear the macOS traffic-light buttons that now overlay this bar.
        isTauriMac ? "pl-[76px] pr-3" : "px-3",
      )}
    >
      {/* Left side - Logo and Account info */}
      <div {...dragRegion} className="flex min-w-0 items-center space-x-3">
        {/* Logo (decorative — drag handle under Tauri) */}
        <div
          {...dragRegion}
          className={cn(
            "flex items-center gap-1.5",
            isTauri && "[&_*]:pointer-events-none",
          )}
        >
          <VybeLogo className="h-5 w-5" decorative />
          <span className="sr-only">Vybe Trader</span>
        </div>

        {/* Account info with connection status. The wrapper (gaps) drags; the
            interactive account button keeps its own pointer events. */}
        <div {...dragRegion} className="flex min-w-0 items-center space-x-2">
          <AccountsSheet
            trigger={createTriggerButton()}
            isOpen={isAccountsSheetOpen}
            onOpenChange={setIsAccountsSheetOpen}
          />
          {isConnected && <InvestorBadge />}
          {isConnected && <AccountMetricsStrip draggableRegion={isTauri} />}
        </div>
      </div>

      {/* Right side - Layout toggles, theme switcher and Settings icon */}
      <div {...dragRegion} className="ml-auto flex items-center space-x-2">
        {/* Quotes sidebar toggle */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-6 w-6 p-0",
            quotesVisible && "bg-primary/10 text-primary"
          )}
          onClick={toggleQuotes}
          title={quotesVisible ? "Hide quotes sidebar" : "Show quotes sidebar"}
          aria-pressed={quotesVisible}
        >
          <PanelLeft className="h-4 w-4" />
        </Button>

        {/* Bottom bar toggle */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-6 w-6 p-0",
            footerVisible && "bg-primary/10 text-primary"
          )}
          onClick={toggleFooter}
          title={footerVisible ? "Hide bottom bar" : "Show bottom bar"}
          aria-pressed={footerVisible}
        >
          <PanelBottom className="h-4 w-4" />
        </Button>

        {/* Chart area layout (1 / 2H / 2V / 4) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              title="Chart layout"
              aria-label="Chart layout"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              onClick={() => setChartLayoutMode("single")}
              className={cn(chartLayoutMode === "single" && "bg-muted")}
            >
              <Square className="mr-2 h-4 w-4" />
              {chartLayoutLabels.single}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setChartLayoutMode("two-horizontal")}
              className={cn(
                chartLayoutMode === "two-horizontal" && "bg-muted",
              )}
            >
              <Columns2 className="mr-2 h-4 w-4" />
              {chartLayoutLabels["two-horizontal"]}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setChartLayoutMode("two-vertical")}
              className={cn(chartLayoutMode === "two-vertical" && "bg-muted")}
            >
              <Rows2 className="mr-2 h-4 w-4" />
              {chartLayoutLabels["two-vertical"]}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setChartLayoutMode("quad")}
              className={cn(chartLayoutMode === "quad" && "bg-muted")}
            >
              <LayoutGrid className="mr-2 h-4 w-4" />
              {chartLayoutLabels.quad}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Agent sidebar toggle */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-6 w-6 p-0",
            agentSidebarVisible && "bg-primary/10 text-primary"
          )}
          onClick={toggleAgentSidebar}
          title={
            agentSidebarVisible ? "Hide Vybe Agent" : "Show Vybe Agent"
          }
          aria-pressed={agentSidebarVisible}
        >
          <VybeAgentMark
            size={16}
            tone={agentSidebarVisible ? "primary" : "foreground"}
          />
        </Button>

        {/* Vybe Studio — custom indicators (admin-only) */}
        {isAdmin && (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-6 w-6 p-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              studioOpen && "bg-primary/10 text-primary"
            )}
            onClick={() => setStudioOpen(!studioOpen)}
            title="Vybe Studio — custom indicators"
            aria-pressed={studioOpen}
            aria-label="Vybe Studio"
          >
            <Sparkles className="h-4 w-4" />
          </Button>
        )}

        {/* Theme Switcher */}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          onClick={toggleTheme}
          title="Toggle theme"
          aria-label="Toggle theme"
        >
          <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        </Button>

        {/* Vybe account — session-aware user chip */}
        <HeaderUserChip />

        {/* Settings */}
        <SettingsSheet
          trigger={
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              title="Settings"
              aria-label="Settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          }
          isOpen={isSettingsSheetOpen}
          onOpenChange={setIsSettingsSheetOpen}
        />
      </div>
    </div>
  );
}
